import { isApolloError, useMutation } from '@apollo/client'
import { Alert, ButtonLink } from '@designsforhealth/dfh-react-components'
import { CircularProgress, TextField } from '@material-ui/core'
import * as R from 'ramda'
import React, { useContext, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import styled from 'styled-components'

import {
  ApplyCouponToCartDocument,
  SetDispensaryPromoCodeOnCartDocument,
} from '../../../graphql/magento'
import useIsMounted from '../../../hooks/useIsMounted'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { TokenContext } from '../../../layouts/context'
import {
  appendCartDispensaryPromoCode,
  CartDispensaryPromoCode,
  writeCartDispensaryPromoCodes,
} from '../../../lib/cart/dispensary-promo'
import { invalidCouponCodeMessage, messageFromAddCouponError } from '../../../lib/cart/errors'
import { usePromoCodeUtilities } from '../../../lib/cart/hooks/usePromoCodeUtilities'
import { logAndCaptureException } from '../../../utils/errorTools'
import ErrorUpdatingCartMessage from '../../ErrorUpdatingCartMessage'

import AddButton from '../../../img/rewards/add-button.inline.svg'

const StyledButtonLink = styled(ButtonLink)`
  margin-left: 36px;
  // match bottom margin on form control
  margin-bottom: 10px;
  text-decoration: underline;
`

const StyledTextField = styled(TextField)`
  flex: 1;
  .MuiFormLabel-root {
    font-size: 14px;
  }

  .MuiFilledInput-root {
    border-color: ${({ theme }) => theme.colors.coolGray.cool450};
    background-color: ${({ theme }) => theme.colors.grayscale.white};
    border-radius: 8px;
    &.Mui-focused {
      border-color: ${({ theme }) => theme.colors.brandBlue.dark300};
    }
  }
`

const ApplyButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    margin-right: 8px;
  }
`

interface FormFields {
  promoCode?: string
}

export interface AddPromoCodeProps {
  cartDispensaryPromoCodes: CartDispensaryPromoCode[] | undefined
  cartId: string
  className?: string
  isEmptyCart: boolean
}

const AddPromoCode: React.FC<AddPromoCodeProps> = ({
  cartDispensaryPromoCodes,
  cartId,
  className,
  isEmptyCart,
}) => {
  const token = useContext(TokenContext)
  const [addCoupon, { loading: applyCouponLoading, error }] = useMutation(
    ApplyCouponToCartDocument,
    {
      context: { token },
    },
  )
  const [setDispensaryPromoCode, { loading: setDispensaryPromoCodeLoading }] = useMutation(
    SetDispensaryPromoCodeOnCartDocument,
    {
      context: { token },
    },
  )
  const [mockLoading, setMockLoading] = useState(false)
  const isMounted = useIsMounted()
  useLogAndCaptureError(
    error,
    (err) => typeof err !== 'string' && !err.message.includes('coupon code'),
  )
  const formMethods = useForm<FormFields>({
    defaultValues: {
      promoCode: '',
    },
  })
  const { control, errors, handleSubmit, reset, setError } = formMethods
  const {
    isCodeRestrictedFromUIInput,
    loading: utilitiesLoading,
    translateCode,
  } = usePromoCodeUtilities({ cartDispensaryPromoCodes })

  const setInvalidCouponCodeAfterDelay = () => {
    setMockLoading(true)
    // simulate request
    setTimeout(() => {
      if (!isMounted.current) {
        return
      }
      setError('promoCode', { type: 'validate', message: invalidCouponCodeMessage })
      setMockLoading(false)
    }, 3000)
  }

  const onSubmit: SubmitHandler<FormFields> = R.pipe(
    ({ promoCode }) => promoCode ?? '',
    R.toUpper,
    R.trim,
    async (promoCode): Promise<void> => {
      // instead of requiring the field and displaying an error state when empty, just
      // check for non-empty value on submit
      if (!promoCode) {
        reset()
        return
      }

      if (await isCodeRestrictedFromUIInput(promoCode)) {
        setInvalidCouponCodeAfterDelay()
        return
      }

      const translateCodeResult = await translateCode(promoCode)

      const { errors: mutationErrors } = await addCoupon({
        variables: {
          cartId,
          couponCode:
            translateCodeResult._tag === 'translated'
              ? translateCodeResult.translatedCode
              : translateCodeResult.inputCode,
        },
      })

      const [mutationError] = mutationErrors ?? []
      // Do not reset form when operation unsuccessful
      if (mutationError) {
        setError('promoCode', {
          type: 'server',
          message: messageFromAddCouponError(mutationError),
        })
        return
      }

      if (translateCodeResult._tag === 'translated') {
        await setDispensaryPromoCode({
          variables: {
            cartId,
            dispensaryPromoCode: writeCartDispensaryPromoCodes(
              appendCartDispensaryPromoCode(
                {
                  dispensaryCode: translateCodeResult.inputCode,
                  rootCode: translateCodeResult.translatedCode,
                },
                cartDispensaryPromoCodes ?? [],
              ),
            ),
          },
        })
      }

      reset()
    },
    R.otherwise((rawErr: unknown) => {
      // ensure error is instance of Error
      const err: Error = rawErr instanceof Error ? rawErr : new Error(`addCoupon: ${rawErr}`)
      // log and capture errors (e.g. Web Crypto API errors)
      logAndCaptureException(err)
      setError('promoCode', {
        type: 'server',
        message: 'An application error occurred, and we have been notified.',
      })
    }),
  )

  const disableAddPromoCode =
    applyCouponLoading ||
    isEmptyCart ||
    mockLoading ||
    setDispensaryPromoCodeLoading ||
    utilitiesLoading

  return (
    <form className={className} noValidate onSubmit={handleSubmit(onSubmit)}>
      {error && isApolloError(error) && !!error.networkError && (
        <Alert type="error">
          <ErrorUpdatingCartMessage />
        </Alert>
      )}
      <Controller
        render={({ name: inputName, onBlur, onChange, ref, value }) => (
          <StyledTextField
            autoComplete="off"
            disabled={disableAddPromoCode}
            error={!!errors[inputName as keyof FormFields]}
            helperText={errors[inputName as keyof FormFields]?.message}
            label="Enter promo code"
            name={inputName}
            InputProps={{
              disableUnderline: true,
            }}
            inputRef={ref}
            onBlur={onBlur}
            onChange={onChange}
            value={value}
            variant="filled"
          />
        )}
        control={control}
        defaultValue=""
        name="promoCode"
      />
      <StyledButtonLink type="submit" disabled={disableAddPromoCode}>
        {applyCouponLoading || mockLoading || setDispensaryPromoCodeLoading ? (
          <CircularProgress size={16} />
        ) : (
          <ApplyButtonContainer>
            <AddButton />
            Apply
          </ApplyButtonContainer>
        )}
      </StyledButtonLink>
    </form>
  )
}

const StyledAddPromoCode = styled(AddPromoCode)({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  position: 'relative',
  width: '100%',
})

export default StyledAddPromoCode
